import React, { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Bikupor from './carousel-sections/Bikupor';
import OmOss from './carousel-sections/OmOss';
import Produkter from './carousel-sections/Produkter';
import BikuporDetails from './carousel-sections/carousel-details/BikuporDetails';
import OmOssDetails from './carousel-sections/carousel-details/OmOssDetails';
import { LockDragPlugin } from './lib/LockDragPlugin';
import '../styles/carousel.css';

export interface CarouselHandle {
  scrollToSlide: (index: number) => void;
}

const Carousel = forwardRef<CarouselHandle>((_, ref) => {
  // Plugin to lock dragging when modal for a product is open
  const lockDragPlugin = useMemo(() => LockDragPlugin(), []);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    dragFree: false,
    containScroll: 'trimSnaps',
    dragThreshold: 0.1,
  },
  [lockDragPlugin]);


  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [showSlideMenu, setShowSlideMenu] = useState(false);
  const indicatorRef = useRef<HTMLDivElement>(null);

  // Expose the scrollToSlide function via the ref
  useImperativeHandle(ref, () => ({
    scrollToSlide: (index: number) => {
      if (emblaApi) {
        emblaApi.scrollTo(index); // Use Embla's API to scroll to the slide
      } else {
        console.warn('Embla API is not initialized yet.');
      }
    },
  }));

  // Lock dragging when products modal is open
  useEffect(() => {
    if (isProductsOpen) {
      lockDragPlugin.lock();
    } else {
      lockDragPlugin.unlock();
    }
  }, [isProductsOpen, lockDragPlugin]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showSlideMenu &&
        indicatorRef.current &&
        !indicatorRef.current.contains(event.target as Node)
      ) {
        setShowSlideMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSlideMenu]);

  useEffect(() => {
  if (!emblaApi) return;
    const update = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };

    emblaApi.on('select', update);
    update(); // trigger once on mount
  }, [emblaApi]);

  // avoid background scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = ''; // Ensure it's reset on unmount
    };
  }, [isModalOpen]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  const tabItems = [
    {
      label: 'Våra Produkter',
      content: <Produkter onRequestDetails={setIsProductsOpen} />,
      modal: null
    },
    {
      label: 'Våra bikupor',
      content: <Bikupor onRequestDetails={() => setIsModalOpen(true)}/>,
      modal: <BikuporDetails onBack={() => setIsModalOpen(false)} />
    },
    {
      label: 'Om oss',
      content: <OmOss onRequestDetails={() => setIsModalOpen(true)} />,
      modal: <OmOssDetails onBack={() => setIsModalOpen(false)} />
    }
  ];
  
  return (
  <div style={{ position: 'relative' }}>
    {/* Keep carousel always mounted */}
    <div
      style={{
        visibility: isModalOpen ? 'hidden' : 'visible',
        pointerEvents: isModalOpen ? 'none' : 'auto',
        transition: 'filter 0.3s ease'
      }}
    >
      <div
        ref={indicatorRef}
        className="slide-indicator cursor-pointer relative inline-block"
        onClick={() => setShowSlideMenu(!showSlideMenu)}
      >
        {selectedIndex + 1}
        <span
          style={{
            display: 'inline-block',
            width: '1px',
            height: '2.2rem',
            backgroundColor: '#262829',
            margin: '0 0.5rem',
            verticalAlign: 'middle',
            opacity: 1
          }}
        />
        <span style={{ fontWeight: 400, padding: '0 0.25rem' }}>{tabItems.length}</span>

        {showSlideMenu && (
          <div className="absolute top-full left-1/2 mt-2 transform translate-x-0 z-50 text-sm text-left whitespace-nowrap rounded-md border border-gray-300 bg-[#fff8e8] shadow-md animate-dropdownFadeIn">
            {tabItems.map((item, index) => (
              <div
                key={index}
                className="px-4 py-2 cursor-pointer group"
                onClick={() => {
                  emblaApi?.scrollTo(index);
                  setShowSlideMenu(false);
                }}
              >
                <span style={{ color: '#6e9d5b' }}>{index + 1} / </span>
                <span className="text-[#c89c00] group-hover:text-[#6e9d5b] transition-colors">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="embla__viewport"
        ref={emblaRef}
      >
        <div className="embla__container">
          {tabItems.map((item, index) => (
            <div className="embla__slide" key={index}>
              {item.content}
            </div>
          ))}
        </div>
      </div>

      {canScrollPrev && !isProductsOpen && (
        <button onClick={scrollPrev} className="carousel-button prev">
          <img src="../../pictures/assets/prevbeeiconnobackground.webp" alt="Previous" />
        </button>
      )}

      {canScrollNext && !isProductsOpen && (
        <button onClick={scrollNext} className="carousel-button next">
          <img src="../../pictures/assets/nextbeeiconnobackground.webp" alt="Next" />
        </button>
      )}
    </div>

    {/* Modal remains on top */}
    {isModalOpen && tabItems[selectedIndex].modal}
  </div>
);

});

export default Carousel;